import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import wooRestApiService from "@/api/services/wooRestApiService";
import useAppDispatch from "@/hooks/useAppDispatch";
import { CACHE_CONFIG, isCacheValid } from "@/utils/cacheManager";
import { fetchAllWooData } from "@/helpers/helpers";
import { handleApiError } from "@/helpers/apiErrorHelper";
import type { RootState } from "@/store/store";
import type { CacheItem } from "@/store/cacheReducer";
import type { ProductCategory } from "@/types/productCategory";

interface CategoryViewModel {
  categories: ProductCategory[];
  currentCategory: ProductCategory | null;
  isLoadingCategories: boolean;
  checkIsActiveCategory: (categorySlug?: string) => boolean;
}

// 處理分類資料，將其轉換為階層結構並排序。
const buildCategoryTree = (categories: ProductCategory[]) => {
  const categoryMap = new Map<number, ProductCategory>();
  const tree: ProductCategory[] = [];

  // 建立分類的 Map
  categories.forEach((category) => {
    category.children = [];
    categoryMap.set(category.id, category);
  });

  // 建立階層結構
  categories.forEach((category) => {
    if (category.parent === 0) {
      tree.push(category);
    } else {
      const parent = categoryMap.get(category.parent);
      if (parent) {
        parent.children.push(category);
      }
    }
  });

  // 遞迴排序函數
  const sortCategories = (categories: ProductCategory[]) => {
    categories.sort((a, b) => a.menu_order - b.menu_order);
    categories.forEach((category) => sortCategories(category.children));
  };

  sortCategories(tree);
  return tree;
};

const findCategoryBySlugRecursive = (
  categories: ProductCategory[],
  slug: string | undefined,
): ProductCategory | null => {
  for (const category of categories) {
    if (category.slug === slug) {
      return category;
    }
    const foundInChildren = findCategoryBySlugRecursive(category.children, slug);
    if (foundInChildren) {
      return foundInChildren;
    }
  }
  return null;
};

const useCategories = ({
  currentCategorySlug,
}: {
  currentCategorySlug: string | undefined;
}): CategoryViewModel => {
  const appDispatch = useAppDispatch();

  const categoryCacheItem = useSelector(
    (state: RootState) => state.cache[CACHE_CONFIG.categories.key],
  );

  const hasValidCache = useMemo(
    () => categoryCacheItem && isCacheValid(categoryCacheItem),
    [categoryCacheItem],
  );

  const categories: ProductCategory[] = useMemo(
    () => (hasValidCache ? buildCategoryTree(categoryCacheItem.data) : []),
    [hasValidCache, categoryCacheItem],
  );

  const [isLoading, setIsLoading] = useState(false);

  const currentCategory = useMemo(
    () => findCategoryBySlugRecursive(categories, currentCategorySlug),
    [categories, currentCategorySlug],
  );

  const checkIsActiveCategory = (categorySlug?: string) => {
    return currentCategorySlug === categorySlug;
  };

  const fetchCategories = async (signal?: AbortSignal) => {
    setIsLoading(true);

    try {
      const allCategories = await fetchAllWooData(
        wooRestApiService.product.categories.getAll,
        undefined,
        undefined,
        signal,
      );

      const cacheItem: CacheItem = {
        data: allCategories, // 存 raw data，使用時再依各自情境轉換格式
        expiry: CACHE_CONFIG.categories.expiry,
        lastFetched: Date.now(),
      };
      appDispatch.setCache(CACHE_CONFIG.categories.key, cacheItem);
    } catch (error) {
      if (signal?.aborted) return;
      handleApiError(error, appDispatch, "取得分類資料失敗，請稍後再試");
    } finally {
      if (signal?.aborted) return;
      setIsLoading(false);
    }
  };

  // 如果快取無效或不存在，發起請求
  useEffect(() => {
    const controller = new AbortController();

    if (!hasValidCache) {
      fetchCategories(controller.signal);
    }

    return () => {
      controller.abort();
    };
  }, [hasValidCache]);

  return {
    categories,
    currentCategory,
    isLoadingCategories: !hasValidCache || isLoading,
    checkIsActiveCategory,
  };
};

export default useCategories;
